import React, { useEffect, useState } from 'react';
import {
  Box,
  styled,
  TableContainer,
  Typography,
  Switch,
  Button,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  FormGroup,
  Checkbox,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Column, CustomTable } from '../../components/CustomTable';
import { Location } from '../../models';
import { theme } from '../../styles/theme';
import { useEmergencyHook } from './store';
import { DEFAULT_ID, StatusEmergencyTeam } from './type';
import { CircularLoading } from '../Users/Group/components';
import LocationFilter from './components/LocationFilter';
import ClearIcon from '@mui/icons-material/Clear';
import { $get } from '../../utils/http';

import { dimensions } from '../../styles/dimensions';
import { useAppContext } from '../../appContext';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import LogoEntradaWithShield from '../../assets/image/LogoEntrada_Diap+schildje.png';
import BodyTableCell from '../Report/LogHistory/GridTable/BodyTableCell';
import { CellAlignment } from '../../types/cell';
import { EnhancedFullscreen } from './components/EnhancedFullscreen';
import { FullscreenConfig } from './types/FullscreenConfig';

const { grey } = theme.palette;
const { rowHeight, base3XL } = dimensions;

// API response interface
interface EmergencyTeamResponse {
  data: {
    users: EmergencyTeamUser[];
  };
  code: string;
  message: null | string;
  fromId: number;
  take: number;
  total: string;
}

// User interface
interface EmergencyTeamUser {
  userId: string;
  userFullName: string;
  location: string;
  lastScan: string;
  lastDoor: string;
  lastDoorName: string;
  presence: string;
  userLocation: string;
}

const ROWS_PER_PAGE = 15;

// Voeg interface toe voor de FullscreenView props
interface FullscreenViewProps {
  bgcolor: string;
  children: React.ReactNode;
}

// Helper functie verplaatsen naar boven, voor gebruik in styled component
const adjustColor = (color: string, amount: number) => {
  const hex = color.replace('#', '');
  const r = Math.min(255, parseInt(hex.substring(0, 2), 16) + amount);
  const g = Math.min(255, parseInt(hex.substring(2, 4), 16) + amount);
  const b = Math.min(255, parseInt(hex.substring(4, 6), 16) + amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Update FullscreenView met correcte typing
const FullscreenView = styled(Box)<FullscreenViewProps>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: ${props => props.bgcolor};
  background-image: linear-gradient(
    120deg,
    ${props => props.bgcolor} 0%,
    ${props => adjustColor(props.bgcolor, 20)} 100%
  );
  z-index: 9999;
  display: flex;
  flex-direction: column;
  padding: 20px;
  overflow: auto;
`;

const FullscreenTitle = styled(Typography)`
  color: white;
  font-weight: 300;
  letter-spacing: 0.5px;
  font-size: 2rem;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 40px;
`;

const FullscreenGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  padding: 40px;
  margin-top: 40px;
  animation: fadeIn 0.5s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const NameCard = styled(Box)`
  padding: 32px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 140px;
  backdrop-filter: blur(8px);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  }
`;

const MainText = styled(Typography)`
  font-size: 48px;
  font-weight: 500;
  color: #698223;
  line-height: 1.2;
`;

const SubText = styled(Typography)`
  font-size: 24px;
  opacity: 0.8;
  font-weight: 400;
  color: #698223;
`;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    padding: 16px;
    min-width: 300px;
  }
`;

const LogoContainer = styled(Box)`
  position: fixed;
  right: 40px;
  bottom: 40px;
  width: 300px;
  height: 61px;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1));
  opacity: 0.9;
  transition: all 0.3s ease;
  cursor: pointer;
  z-index: 10000;

  &:hover {
    opacity: 1;
    transform: scale(1.02);
  }

  & > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const SettingsPopup = styled(Box)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  z-index: 10001;
  min-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PopupOverlay = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

const PresentTeam: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [{ locationList, emergencyList }, { getLocations, getEmergencyTeams }] = useEmergencyHook();
  const { locations } = useAppContext();

  const [loading, setLoading] = useState<boolean>(true);
  const [selectedLocations, setSelectedLocations] = useState<Location[]>([]);
  const [emergencyMembers, setEmergencyMembers] = useState<EmergencyTeamUser[]>([]);
  const [showAllMembers, setShowAllMembers] = useState<boolean>(true);
  const [showAbsent, setShowAbsent] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  const [showLastDoor, setShowLastDoor] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState('#698223');
  const [fullscreenConfig, setFullscreenConfig] = useState<FullscreenConfig | null>(null);

  const fetchEmergencyTeam = async () => {
    try {
      const response: EmergencyTeamResponse = await $get('users/emergencyTeam');
      if (JSON.stringify(response.data.users) !== JSON.stringify(emergencyMembers)) {
        setEmergencyMembers(response.data.users);
      }
    } catch (error) {
      console.error('Error fetching emergency team:', error);
    }
  };

  const fetchFullscreenConfig = async () => {
    try {
      const response = await $get('emergency-team/fullscreen-config');
      setFullscreenConfig(response.data);
    } catch (error) {
      console.error('Error fetching fullscreen config:', error);
    }
  };

  const initData = async () => {
    setLoading(true);
    await Promise.all([getLocations(), fetchEmergencyTeam(), fetchFullscreenConfig()]);
    setLoading(false);
  };

  const columns: Column[] = [
    {
      dataKey: 'name',
      fieldName: t('emergency_team.name')
    },
    {
      dataKey: 'belongsToLocation',
      fieldName: t('emergency_team.belongs_to_location')
    },
    {
      dataKey: 'location',
      fieldName: t('emergency_team.registered_at_location')
    },
    {
      dataKey: 'last scanned door',
      fieldName: t('emergency_team.last_scanned_door')
    }
  ];

  const sortListByStatus = (members: EmergencyTeamUser[]) => {
    return [...members].sort((a, b) => {
      if (a.presence === 'present' && b.presence !== 'present') return -1;
      if (a.presence !== 'present' && b.presence === 'present') return 1;
      return 0;
    });
  };

  const getFilteredMembers = (members: EmergencyTeamUser[]) => {
    return members.filter(member => {
      const matchesPresence = showAbsent ? true : member.presence === 'present';
      const matchesLocation = selectedLocation === 'all' ? true : member.location === selectedLocation;
      return matchesPresence && matchesLocation;
    });
  };

  useEffect(() => {
    console.log('Calling initData');
    initData();

    const intervalId = setInterval(() => {
      fetchEmergencyTeam();
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    console.log('Emergency Members updated:', emergencyMembers);
    console.log('Sorted data:', sortListByStatus(emergencyMembers));
  }, [emergencyMembers, showAllMembers]);

  const isFilterApplied = selectedLocations.length > 0 || !showAllMembers;

  const fillEmptyRows = (dataList: EmergencyTeamUser[]) => {
    const currentRows = dataList.length;
    const emptyRowsNeeded = ROWS_PER_PAGE - currentRows;

    if (emptyRowsNeeded <= 0) return dataList;

    const emptyRows = Array(emptyRowsNeeded).fill({
      id: 'empty-' + Math.random(),
      name: '',
      location: '',
      'last scanned door': '',
      status: '',
      locationId: '',
      isEmpty: true
    });

    return [...dataList, ...emptyRows];
  };

  const getColumnWidth = (column: string) => {
    switch (column) {
      case 'name':
        return '25%'; // Percentage in plaats van vaste breedte voor betere responsive layout
      case 'belongsToLocation':
        return '25%';
      case 'location':
        return '25%';
      case 'lastDoor':
        return '25%';
      default:
        return '25%';
    }
  };

  const StyledTable = styled(Table)`
    height: 100%;
    table-layout: fixed; // Dit zorgt ervoor dat de kolommen vast staan
    ${props => props.theme.breakpoints.down('xl')} {
      min-height: 300px;
    }
    ${props => props.theme.breakpoints.down('md')} {
      justify-content: space-between;
    }
  `;

  const Container = styled(TableContainer)`
    border: 1px solid ${grey[400]};
    height: calc(100% - 2px);
    width: calc(100% - 2px);
    overflow: auto;
  `;

  const InformationRow = styled(TableRow)`
    background-color: ${({ theme }) => theme.palette.common.white};
    &:nth-of-type(odd) {
      background-color: ${grey[500]};
    }
    transition: background-color 0.5s ease;
    height: 30px;
  `;

  const InformationCell = styled(TableCell)<{ columnId?: string }>`
    font-weight: 500;
    font-size: 14px;
    padding: 8px;
    height: 30px;
    width: ${({ columnId }) => getColumnWidth(columnId || '')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left; // Expliciete text-align
  `;

  const StyledTableHead = styled(TableHead)`
    height: ${rowHeight};
  `;

  const Component = styled(Box)({
    '& .MuiTableRow-root': {
      height: '10px !important'
    },
    '& .MuiTableCell-root': {
      padding: '0 !important',
      height: '10px !important'
    },
    '& .empty-row': {
      backgroundColor: '#f5f5f5',
      '& td': {
        border: 'none'
      }
    }
  });

  const PageContainer = styled(Box)`
    width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;
  `;

  const Wrapper = styled(Box)`
    display: flex;
    flex-direction: column;
    background-color: ${grey[500]};
    height: 100%;
    overflow: auto;
    flex: 1;
  `;

  const Header = styled(Box)`
    background-color: white;
    height: ${base3XL};
    display: flex;
    align-items: center;
  `;

  const Body = styled(Box)`
    background-color: white;
    height: 100%;
    overflow: auto;
  `;

  const TitleCell = styled(TableCell)<{ columnId?: string }>`
    color: ${grey[100]};
    font-weight: 700;
    font-size: 16px;
    padding: 8px;
    height: 30px;
    width: ${({ columnId }) => getColumnWidth(columnId || '')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left; // Expliciete text-align
  `;

  const FilterContainer = styled(Box)`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-left: 16px;
    height: 40px;
  `;

  const LocationFilter = styled(FormControl)`
    min-width: 200px;
    height: 40px;

    .MuiOutlinedInput-root {
      height: 40px;
    }

    .MuiInputLabel-root {
      line-height: 0.8;
    }
  `;

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSettings(true);
  };

  if (isFullscreen && fullscreenConfig) {
    return (
      <EnhancedFullscreen
        config={fullscreenConfig}
        emergencyTeamData={emergencyMembers}
        onClose={() => setIsFullscreen(false)}
      />
    );
  }

  return (
    <PageContainer>
      <Wrapper>
        <Header>
          <FilterContainer>
            <LocationFilter size="small">
              <InputLabel>Locatie</InputLabel>
              <Select value={selectedLocation} onChange={e => setSelectedLocation(e.target.value)} label="Locatie">
                <MenuItem value="all">Alle locaties</MenuItem>
                {locations.map((location: Location) => (
                  <MenuItem key={location.id} value={location.location}>
                    {location.location}
                  </MenuItem>
                ))}
              </Select>
            </LocationFilter>
            <FormControlLabel
              control={
                <Switch
                  checked={showAbsent}
                  onChange={e => setShowAbsent(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={t('emergency_team.show_absent')}
            />
            <Button
              startIcon={<FullscreenIcon />}
              onClick={() => setIsFullscreen(true)}
              variant="outlined"
              size="small">
              {t('fullscreen')}
            </Button>
          </FilterContainer>
        </Header>
        <Body>
          <Container>
            <StyledTable>
              <StyledTableHead>
                <TableRow>
                  <TitleCell columnId="name">{t('emergency_team.name')}</TitleCell>
                  <TitleCell columnId="belongsToLocation">{t('emergency_team.belongs_to_location')}</TitleCell>
                  <TitleCell columnId="location">{t('emergency_team.location')}</TitleCell>
                  <TitleCell columnId="lastDoor">{t('emergency_team.last_scanned_door')}</TitleCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {sortListByStatus(getFilteredMembers(emergencyMembers)).map(member => (
                  <InformationRow key={member.userId}>
                    <BodyTableCell value={member.userFullName} link={`/users/user/${member.userId}`} isIcon={true} />
                    <InformationCell columnId="belongsToLocation">{member.userLocation}</InformationCell>
                    <InformationCell columnId="location">{member.location}</InformationCell>
                    <InformationCell columnId="lastDoor">{member.lastDoorName || 'Geen'}</InformationCell>
                  </InformationRow>
                ))}
                {Array.from({ length: Math.max(10 - getFilteredMembers(emergencyMembers).length, 0) }).map(
                  (_, index) => (
                    <InformationRow key={`empty-${index}`}>
                      <InformationCell columnId="name">&nbsp;</InformationCell>
                      <InformationCell columnId="belongsToLocation">&nbsp;</InformationCell>
                      <InformationCell columnId="location">&nbsp;</InformationCell>
                      <InformationCell columnId="lastDoor">&nbsp;</InformationCell>
                    </InformationRow>
                  )
                )}
              </TableBody>
            </StyledTable>
          </Container>
        </Body>
      </Wrapper>
    </PageContainer>
  );
};

export default PresentTeam;
